import React, { Component, Fragment } from 'react';
import { components, shared } from '@kurtosys/ksys-app-components';
import { inject, observer } from 'mobx-react';

import { IFilterMenu } from '../../models/input/IFilterMenu.js';
import { IFilterMenuOption } from '../../models/input/IFilterMenuOption.js';
import { AppStore } from '../App/stores/AppStore.js';
import { InjectedStyledComponent } from '../shared/InjectedStyledComponent.js';

import { IMegaMenuProps, IMegaMenuState } from './models/index.js';
import Button from './styledComponents/Button.js';
import ButtonLabel from './styledComponents/ButtonLabel.js';
import ClearAllButton from './styledComponents/ClearAllButton';
import Heading from './styledComponents/Heading.js';
import MenuCheckbox from './styledComponents/MenuCheckbox';
import MenuClearButton from './styledComponents/MenuClearButton.js';
import MenuDescription from './styledComponents/MenuDescription.js';
import MenuDivider from './styledComponents/MenuDivider.js';
import MenuLabel from './styledComponents/MenuLabel.js';
import Pill from './styledComponents/Pill.js';
import Wrapper from './styledComponents/Wrapper.js';
import { megaMenuStyle } from './styles/MegaMenu.styles.js';

const Translate = components.base.Translate.Translate;

type IMenuProps = components.base.Menu.models.IMenuProps;
type TPortalToggleReason = shared.TPortalToggleReason;

const KeyboardListHandler = shared.KeyboardListHandler;
const MenuList = components.base.MenuList.MenuList;
const MenuItem = components.base.MenuItem.MenuItem;
const Menu = components.base.Menu.Menu;
// TODO: fix filters apply and input events emit correctly (across all filter inputs)
// TODO: - filters with brackets are failing (Kelly to reach out to Blue Team)...

class RawMegaMenu extends Component<IMegaMenuProps, IMegaMenuState> {
	static configurationKey = 'megaMenu' as const;
	static styleKey = 'megaMenu' as const;
	buttonElement = React.createRef<HTMLButtonElement>();
	popoverElement = React.createRef<HTMLDivElement>();
	constructor(props: IMegaMenuProps) {
		super(props);
		this.state = {
			open: false,
			menuAnchorEl: null,
			focusedMenuIndex: 0,
			focusedMenuItemIndex: 0,
		};
	}

	getKeyboardHandler() {
		return new KeyboardListHandler({
			axis: {
				vertical: {
					pageSize: Math.round(this.getMenuItemLength() / 2),
					getLength: this.getMenuItemLength,
					getFocus: () => this.state.focusedMenuItemIndex,
					setFocus: (index) => this.setState({ focusedMenuItemIndex: index }),
				},
				horizontal: {
					getLength: this.getMenuLength,
					getFocus: () => this.state.focusedMenuIndex,
					setFocus: (index) => this.setState({ focusedMenuIndex: index, focusedMenuItemIndex: 0 }),
				},
				homeEnd: 'vertical',
				page: 'vertical',
			},
			selection: {
				toggle: (open: boolean, reason, e?: React.KeyboardEvent<HTMLElement>) => {
					if (!open) {
						this.handleClose(undefined, reason);
						this.buttonElement?.current?.focus();
					} else {
						this.setState({ open: true, menuAnchorEl: e ? e.currentTarget : null });
					}
				},
				onSelect: () => {
					const menuIndex = this.state.focusedMenuIndex;
					const itemIndex = this.state.focusedMenuItemIndex;
					if (this.getMenuIndexType(menuIndex, itemIndex) === 'clearButton') {
						this.clearMenu(menuIndex);
					}
					else {
						this.toggleSelectedItem(menuIndex, itemIndex);
					}
				},
			},
		});
	}

	getMenuLength = () => {
		const { megaMenuStore } = this.props;
		if (megaMenuStore) {
			const { filterMappings } = megaMenuStore;
			return filterMappings.size;
		}
	};

	getFocusedMenu = (): IFilterMenu | undefined => {
		return this.getMenuByIndex(this.state.focusedMenuIndex);
	};

	getMenuByIndex = (index: number): IFilterMenu | undefined => {
		const { megaMenuStore } = this.props;
		if (megaMenuStore) {
			const { filterMappings } = megaMenuStore;
			const list = Array.from(filterMappings.values());
			if (list && list[index]) {
				return list[index];
			}
		}
		return;
	};

	getMenuItemByIndexes = (menuIndex: number, menuItemIndex: number): IFilterMenuOption | undefined => {
		const menu = this.getMenuByIndex(menuIndex);
		if (menu) {
			return menu.options[menuItemIndex];
		}
		return;
	};

	getMenuIndexType = (menuIndex: number, menuItemIndex: number): 'menuItem' | 'clearButton' => {
		const menu = this.getMenuByIndex(menuIndex);
		if (menu.displayClearButton && menuItemIndex === menu.options.length) {
			return 'clearButton';
		}
		return 'menuItem';
	}

	getMenuItemLength = () => {
		const focusedMenu = this.getFocusedMenu();
		if (focusedMenu) {
			return focusedMenu.options.length + (focusedMenu.displayClearButton ? 1 : 0);
		}
	};

	handleButtonClick = (e: React.MouseEvent<HTMLElement>) => {
		this.setState({ open: true, menuAnchorEl: e.currentTarget });
	};

	handleItemClick = (e: React.MouseEvent<HTMLElement>, menuIndex: number, itemIndex: number) => {
		this.toggleSelectedItem(menuIndex, itemIndex);
	};

	toggleSelectedItem = (menuIndex: number, itemIndex: number) => {
		const { toggleValue } = this.props.megaMenuStore;
		this.setState({ focusedMenuIndex: menuIndex, focusedMenuItemIndex: itemIndex });
		if (toggleValue) {
			const selectedMenu = this.getMenuByIndex(menuIndex);
			const selectedMenuItem = this.getMenuItemByIndexes(menuIndex, itemIndex);
			if (selectedMenu && selectedMenuItem) {
				toggleValue(selectedMenu.key, selectedMenuItem);
			}
		}
	};

	clearMenu = (menuIndex: number) => {
		const { clearValues } = this.props.megaMenuStore;
		if (clearValues) {
			const selectedMenu = this.getMenuByIndex(menuIndex);
			clearValues(selectedMenu.key);
		}
	};

	handleClose = (e: React.MouseEvent<HTMLElement> | undefined, reason: TPortalToggleReason) => {
		if (this.props.megaMenuStore) {
			const { storeContext, menuId } = this.props.megaMenuStore;
			const appStore = storeContext.get<AppStore>('appStore');
			if (appStore) {
				appStore.logDebug('megaMenu.close', {
					menuId,
					reason,
				});
			}
		}
		this.setState({ open: false, menuAnchorEl: null });
	};

	getMenuLabel(listKey: string, label?: string) {
		return label ? <MenuLabel key={`${listKey}-label`}>{label}</MenuLabel> : null;
	}

	getMenuDescription(listKey: string, description?: string, isHtml = false) {
		let menuDescription = null;
		if (description) {
			if (isHtml) {
				menuDescription = (
					<MenuDescription key={`${listKey}-desc`} dangerouslySetInnerHTML={{ __html: description }} />
				);
			} else {
				menuDescription = <MenuDescription key={`${listKey}-desc`}>{description}</MenuDescription>;
			}
		}
		return menuDescription;
	}

	getMenuItemCheckbox(isMultiSelect: boolean, checked: boolean) {
		if (isMultiSelect) {
			return (
				<MenuCheckbox
					asset={checked ? 'base.checkbox.checked' : 'base.checkbox.unchecked'}
					aria-hidden="true"
				/>
			);
		}
		return null;
	}

	render() {
		const { className, megaMenuStore } = this.props;

		if (!megaMenuStore || !megaMenuStore.show) {
			return null;
		}

		const {
			buttonId,
			buttonLabel,
			buttonPlaceholder,
			buttonCollapsedProps,
			buttonExpandedProps,
			menuId,
			filterMappings,
			selectedItemCount,
			showPill,
			pillProps,
			showClearAllButton,
			clearAllButtonProps,
			getMenuListClearButtonProps,
		} = megaMenuStore;

		if (filterMappings.size <= 0) {
			return null;
		}

		const menus = Array.from(filterMappings);

		const menuProps: IMenuProps = {
			type: 'mega',
			open: this.state.open,
			anchorElement: this.state.menuAnchorEl,
			portalContainer: this.popoverElement,
			disablePortal: false,
			placement: 'bottom-start',
			strategy: 'absolute',
			onClose: this.handleClose,
		};

		const keyboardHandler = this.getKeyboardHandler();
		return (
			<Wrapper className={className}>
				<Heading>
					<ButtonLabel key={'button-label'}>{buttonLabel}</ButtonLabel>
					{showPill && <Pill key={'button-pill'} {...pillProps} />}
					{showClearAllButton && (
						<ClearAllButton key={'clear-button'} {...clearAllButtonProps} aria-label={'Clear All'} />
					)}
				</Heading>
				<Button
					key={'button'}
					{...(this.state.open ? buttonExpandedProps : buttonCollapsedProps)}
					id={buttonId}
					ref={this.buttonElement}
					aria-controls={this.state.open ? menuId : undefined}
					aria-haspopup="true"
					aria-expanded={this.state.open ? 'true' : undefined}
					onClick={this.handleButtonClick}
					onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
						keyboardHandler.onKeyDown(e, this.state.open)
					}
				>
					{selectedItemCount || buttonPlaceholder}
				</Button>
				<div ref={this.popoverElement} />
				{this.state.open && (
					<Menu id={menuId} aria-labelledby={buttonId} {...menuProps}>
						{menus.map(([menuKey, menu], menuIndex) => {
							const listKey = `menu-list-${menuIndex}-${menuKey}`;
							const listLabelId = `${listKey}-label`;
							const listDescId = `${listKey}-desc`;
							const menuOptions = menu.options.reduce((options: IFilterMenuOption[], option) => {
								if (option.visible || option.visible === undefined) {
									options.push(option);
								}
								return options;
							}, []);
							const clearButtonProps = getMenuListClearButtonProps(menuKey);
							return (
								<Fragment key={listKey}>
									<MenuList key={listKey} aria-labelledby={listLabelId} aria-describedby={listDescId}>
										{this.getMenuLabel(listKey, menu.label)}
										{this.getMenuDescription(listKey, menu.description, menu.isDescriptionHTML)}
										{menuOptions.map((item, itemIndex) => {
											const itemKey = `menu-item-${itemIndex}-${item.key}`;
											const hasFocus =
												this.state.focusedMenuIndex === menuIndex &&
												this.state.focusedMenuItemIndex === itemIndex;
											const isSelected = item.checked;
											return (
												<MenuItem
													key={itemKey}
													type={menu.isMultiSelect ? 'checkbox' : 'standard'}
													hasFocus={hasFocus}
													isSelected={isSelected}
													onClick={(e: React.MouseEvent<HTMLElement>) =>
														this.handleItemClick(e, menuIndex, itemIndex)
													}
													onKeyDown={(e: React.KeyboardEvent<HTMLLIElement>) =>
														keyboardHandler.onKeyDown(e, this.state.open)
													}
												>
													<span key={`${itemKey}-span`}>
														{this.getMenuItemCheckbox(menu.isMultiSelect, isSelected)}
														<Translate>{item.label}</Translate>
													</span>
													{item.count || null}
												</MenuItem>
											);
										})}
										{menu.displayClearButton && (
											<>
												<MenuDivider
													key={`${listKey}-clear-button-divider`}
													orientation="horizontal"
												/>
												<MenuClearButton
													key={`${listKey}-clear-button`}
													hasFocus={this.state.focusedMenuIndex === menuIndex && this.state.focusedMenuItemIndex === menu.options.length}
													onKeyDown={(e: React.KeyboardEvent<HTMLButtonElement>) =>
														keyboardHandler.onKeyDown(e, this.state.open)
													}
													{...clearButtonProps}
												>
													{clearButtonProps.text}
												</MenuClearButton>
											</>
										)}
									</MenuList>
									{menuIndex < menus.length - 1 && (
										<MenuDivider key={`${listKey}-divider`} orientation="vertical" />
									)}
								</Fragment>
							);
						})}
					</Menu>
				)}
			</Wrapper>
		);
	}
}

export const MegaMenu = inject('appStore', 'megaMenuStore')(observer(RawMegaMenu));
export default InjectedStyledComponent(MegaMenu, 'megaMenu', undefined, undefined, undefined, undefined, megaMenuStyle);
